import React, { useState, useEffect } from 'react';
import { X, Package, Clock, CheckCircle, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import '../../styles/marketplace.css';
import { marketplaceAPI } from '../../services/marketplaceAPI';

const OrderHistoryModal = ({ isOpen, onClose, selectedCurrency, rates }) => {
    const [orders, setOrders] = useState([]);

    useEffect(() => {
        if (isOpen) {
            // In a real app, you might fetch specific user orders here
            const data = marketplaceAPI.getOrders();
            setOrders(data);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="mp-modal-overlay">
            <div className="mp-modal-content" style={{ maxWidth: '700px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
                <div className="mp-modal-header">
                    <h2>Order History</h2>
                    <button onClick={onClose} className="mp-modal-close"><X size={24} /></button>
                </div>

                <div className="mp-modal-body" style={{ overflowY: 'auto' }}>
                    {orders.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                            <Package size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
                            <p>No past orders found.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {orders.map(order => (
                                <OrderCard
                                    key={order.id}
                                    order={order}
                                    selectedCurrency={selectedCurrency}
                                    rates={rates}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const OrderCard = ({ order, selectedCurrency, rates }) => {
    const [expanded, setExpanded] = useState(false);

    // Currency Conversion for Order Total
    const displayCurrency = selectedCurrency || 'SAR';
    const rate = rates && rates[displayCurrency] ? rates[displayCurrency] : 1;
    const displayTotal = (order.total * rate).toFixed(2);

    const getStatusColor = (status) => {
        switch (status) {
            case 'Delivered': return '#10B981';
            case 'Processing': return '#3B82F6';
            case 'Cancelled': return '#EF4444';
            default: return '#666';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'Delivered': return <CheckCircle size={16} />;
            case 'Processing': return <Clock size={16} />;
            case 'Cancelled': return <XCircle size={16} />;
            default: return <Package size={16} />;
        }
    };

    return (
        <div style={{
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            overflow: 'hidden',
            backgroundColor: '#fff'
        }}>
            <div
                onClick={() => setExpanded(!expanded)}
                style={{
                    padding: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    background: '#FAFAFA',
                    borderBottom: expanded ? '1px solid #E5E7EB' : 'none'
                }}
            >
                <div>
                    <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '4px' }}>
                        {order.id}
                    </div>
                    <div style={{ fontSize: '14px', color: '#666' }}>
                        {order.date}
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '13px',
                        fontWeight: '600',
                        backgroundColor: `${getStatusColor(order.status)}15`,
                        color: getStatusColor(order.status)
                    }}>
                        {getStatusIcon(order.status)}
                        {order.status}
                    </div>

                    <div style={{ fontWeight: 'bold', minWidth: '80px', textAlign: 'right' }}>
                        {displayTotal} {displayCurrency}
                    </div>

                    {expanded ? <ChevronUp size={20} color="#999" /> : <ChevronDown size={20} color="#999" />}
                </div>
            </div>

            {expanded && (
                <div style={{ padding: '16px', backgroundColor: '#fff' }}>
                    <h5 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#333' }}>Items</h5>
                    {order.items.map((item, idx) => (
                        <div key={idx} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: '14px',
                            marginBottom: '8px',
                            color: '#555',
                            borderBottom: idx === order.items.length - 1 ? 'none' : '1px dashed #eee',
                            paddingBottom: idx === order.items.length - 1 ? 0 : '8px'
                        }}>
                            <span>{item.quantity}x {item.title}</span>
                            <span>{((item.price * rate).toFixed(2))} {displayCurrency}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default OrderHistoryModal;
